//context
import { useContext, useEffect, useState } from 'react';
import { ActivityFeedContext } from '@/context/ActivityFeedContext';

//helpers
import { formatActivityTime, formatLongDateGroup, getActiveParentList, getAppDetails, getContentDetails, getExtendedContentDetails, getObjectionableCommentDetails, getParentListDetails, getUniqueActivityKey, getUserDetails } from  '@/helpers/ActivityFeed';

//interfaces
import { IActivityProps, UserActivity } from  '@/interfaces/activityFeed';
import { IMeta } from  '@/interfaces/activityFeedNotifications';

//components
import ContentPost from '../notifications/ContentPost/ContentPost';
import ModeratedComment from '../notifications/ModeratedComment/ModeratedComment';
import RecognitionPost from '../notifications/Recognition/RecognitionPost';



const ModerationActivity = ({zIndexNum, isPopOut, activity: {author, contentItem, parentLists, parentList, isRemovedFromParent, approval, recognition, createdAt, activityType, workspace, objectionableComment, meta}} : IActivityProps) => { 
    const { getNotification, getOpenOnWebLink, likeComment, likeContent, deleteComment, onApproveContentClick, postCommentOnContent, onArchiveContentClick, isMultipleAppOrganisation, userProfileImage} = useContext(ActivityFeedContext);
    const activeParentList = getActiveParentList(parentLists);
    const id = getUniqueActivityKey(activityType, contentItem.id, createdAt, author?.id);
    const notificationDetails = getNotification(id) ?? undefined
    const [contentMeta, setContentMeta] = useState<IMeta>(meta);

    useEffect(() => {
        setContentMeta(meta);
    }, [meta]);

    function handleCommentLike() {
        handleLikeUpdates(true);
    }

    function handleLikeClick() {
        handleLikeUpdates(false);
    }

    function handleLikeUpdates(isComment?: boolean) {
        setContentMeta((prev: IMeta) => {
            const updated = {
                ...prev,
                likeCount: prev.hasLiked ? prev.likeCount - 1 : prev.likeCount + 1,
                hasLiked: !prev.hasLiked
            };
    
            if (isComment) {
                likeComment(id, workspace.id, contentItem.id, objectionableComment.commentId, prev.hasLiked);
            } else {
                likeContent(id, workspace.id, contentItem.id, prev.hasLiked);
            }

            return updated;
        });
    }


    function handleCommentClick(comment: string) {
        postCommentOnContent(id, contentItem.id, comment, workspace.id)
    }

    function handleArchive(){
        onArchiveContentClick( id, parentList?.id, contentItem.id)
    }

    function handleApproval(){
        onApproveContentClick(id, contentItem.id)
    }

    function openObjectionableCommentLink(): string {
        return objectionableComment?.isDeleted 
            ?   getOpenOnWebLink(contentItem.id)
            :   getOpenOnWebLink(contentItem.id, objectionableComment.commentId)
    }

    if (activityType === UserActivity.moderatedContentPosted) { 
        return (
            <ContentPost 
                key={id}
                activityId={id}
                zIndexNum={zIndexNum}
                isPopOut={isPopOut}
                timeStamp={formatActivityTime(createdAt)}
                dateStamp={formatLongDateGroup(createdAt)}
                user={getUserDetails(author)}
                content={getExtendedContentDetails(contentItem, parentList, workspace, contentMeta, parentLists?.length > 1)}
                parentList={getParentListDetails(parentList, workspace)}
                appDetails={getAppDetails(workspace, isMultipleAppOrganisation)}
                meta={contentMeta}
                editorProfileImage={userProfileImage}
                notificationDetails={notificationDetails}
                getOpenOnWebLink={() => getOpenOnWebLink(contentItem.id)}
                onArchiveContentClick={handleArchive}
                onPostCommentClick={contentMeta.commentsEnabled ? postCommentOnContent : undefined}
                onLikeClick={contentMeta.likesEnabled ? handleLikeClick : undefined}
                isRemovedFromParentList={isRemovedFromParent}
                needsApproval={approval.needsApproval} 
                onApproveClick={handleApproval}     
                isModeratedContent={true}      
            />
        )
    }
    
    if (activityType === UserActivity.moderatedRecognition) { 
        return (
            <RecognitionPost
                key={id}
                activityId={id}
                isPopOut={isPopOut}
                timeStamp={formatActivityTime(createdAt)}
                dateStamp={formatLongDateGroup(createdAt)}
                user={getUserDetails(author)} 
                content={getExtendedContentDetails(contentItem, parentList, workspace, meta, parentLists?.length > 1)}
                parentList={getParentListDetails(parentList, workspace)}
                meta={contentMeta}
                recognition={recognition}
                appDetails={getAppDetails(workspace, isMultipleAppOrganisation)} 
                editorProfileImage={userProfileImage}
                onLikeClick={handleLikeClick}
                onPostCommentClick={handleCommentClick}
                getWebLink={() => getOpenOnWebLink(contentItem.id)}
                onArchiveContentClick={handleArchive}
                notificationDetails={notificationDetails}
                isRemovedFromParentList={isRemovedFromParent}
                needsApproval={approval.needsApproval} 
                onApproveClick={handleApproval}     
                isModeratedContent={true}    
            />
        ); 
    }
    
    if (activityType === UserActivity.moderatedComment) { 
        return (
            <ModeratedComment 
                zIndexNum={zIndexNum}
                key={id}
                activityId={id}
                isPopOut={isPopOut}
                timeStamp={formatActivityTime(createdAt)} 
                dateStamp={formatLongDateGroup(createdAt)}
                userWhoFlagged={getUserDetails(author)} 
                content={getContentDetails(contentItem, activeParentList, workspace, parentLists?.length > 1)}    
                meta={contentMeta}
                comment={getObjectionableCommentDetails(objectionableComment)}
                parentList={getParentListDetails(activeParentList, workspace)} 
                appDetails={getAppDetails(workspace, isMultipleAppOrganisation)} 
                onLikeClick={handleCommentLike} 
                getOpenOnWebLink={openObjectionableCommentLink}                      
                notificationDetails={notificationDetails}
                onDeleteCommentClick={deleteComment} 
            />
        ); 
    }
                    
    return null; 
}
export default ModerationActivity